import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { navItems, site } from "../data/site";
import { IconClose, IconPhone, IconMail, IconPin } from "./icons";
import styles from "./MobileNavDrawer.module.css";

export default function MobileNavDrawer({ open, onClose }) {
  // Lock body scroll while drawer is open so iOS Safari doesn't bounce
  // the page underneath. Restore on close + cleanup.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Render via portal — the SiteHeader uses translateX(-50%) for
  // shell centering, which would otherwise become the containing
  // block for any fixed descendant.
  return createPortal(
    <>
      <div
        className={[styles.scrim, open ? styles.scrimOpen : ""].join(" ")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={[styles.drawer, open ? styles.drawerOpen : ""].join(" ")}
        aria-label="Site menu"
        aria-hidden={!open}
        inert={!open ? "" : undefined}
      >
        <div className={styles.head}>
          <Link to="/" onClick={onClose} className={styles.brand}>
            <span className={styles.brandWord}>UPSHOT</span>
            <span className={styles.brandSub}>Architecture</span>
          </Link>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeBtn}
            aria-label="Close menu"
          >
            <IconClose />
          </button>
        </div>

        <nav className={styles.primary} aria-label="Primary">
          <NavLink
            to="/"
            end
            onClick={onClose}
            className={({ isActive }) =>
              [styles.primaryLink, isActive ? styles.active : ""].join(" ")
            }
          >
            Home
          </NavLink>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                [styles.primaryLink, isActive ? styles.active : ""].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.contact}>
          <div className={styles.contactLabel}>Contact</div>
          <a href={`tel:${site.phoneTel}`} className={styles.contactRow}>
            <IconPhone /> <span>{site.phone}</span>
          </a>
          <a href={`mailto:${site.email}`} className={styles.contactRow}>
            <IconMail /> <span>{site.email}</span>
          </a>
          <span className={styles.contactRow}>
            <IconPin /> <span>{site.city}</span>
          </span>
        </div>
      </aside>
    </>,
    document.body
  );
}
